/**
 * Parser para JSON de planificación de turnos
 * Convierte el JSON complejo en un objeto simplificado con employee_ids, start, end y plans
 */
export default class ScheduleParser {
	/**
	 * Parsea un JSON de planificación y extrae la información relevante
	 * @param {string|Object} jsonData - JSON string o objeto parseado
	 * @returns {Object} Objeto con employee_ids, start, end y plans
	 */
	static parse(jsonData) {
		try {
			// Si es string, parsearlo
			const data =
				typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;

			// Parsear el request y response si están como strings
			const request =
				typeof data.request === "string"
					? JSON.parse(data.request)
					: data.request;
			const response =
				typeof data.response === "string"
					? JSON.parse(data.response)
					: data.response;

			// Extraer plans del result_plans
			const plans = response.result_plans || [];

			// Extraer employee_ids únicos de los planes
			const employeeIdsSet = new Set();
			plans.forEach((plan) => {
				if (plan.employee_id) {
					employeeIdsSet.add(plan.employee_id);
				}
			});
			const employee_ids = Array.from(employeeIdsSet).sort((a, b) => a - b);

			// Extraer TODOS los empleados del grupo que participaron en el
			// cálculo (los que están en el `request`), no solo aquellos para
			// los que la IA encontró algún turno. El backend usa `employee_ids`
			// para eliminar los turnos de disponibilidad, por lo que debemos
			// enviar el grupo completo aunque no se les haya asignado turno.
			const request_employee_ids = this.extractRequestEmployeeIds(request);

			// Unión: nunca enviamos menos empleados que los que tienen plan.
			const all_employee_ids = Array.from(
				new Set([...employee_ids, ...request_employee_ids])
			).sort((a, b) => a - b);

			// Rango del periodo planificado: required_needs incluye todos los días
			// del intervalo (aunque no haya turnos asignados). result_plans puede
			// omitir el primer/último día si la IA no asignó nadie ese día.
			const { start, end } = this.extractPlanningDateRange(
				response,
				request,
				plans
			);

			// Mapear los planes al formato deseado
			const mappedPlans = plans.map((plan) => ({
				day: plan.day,
				employee_id: plan.employee_id,
				shift_id: plan.shift_id,
				function_id: plan.function_id,
				ubication_ids: plan.ubication_ids || [],
				locked: plan.locked || false,
				time_intervals: plan.time_intervals || [],
			}));

			return {
				employee_ids,
				request_employee_ids,
				all_employee_ids,
				start,
				end,
				plans: mappedPlans,
			};
		} catch (error) {
			console.error("Error parsing schedule JSON:", error);
			return null;
		}
	}

	/**
	 * Normaliza una fecha a YYYY-MM-DD para comparaciones lexicográficas.
	 * @param {string|number|Date} date
	 * @returns {string|null}
	 */
	static normalizePlanningDate(date) {
		if (!date) return null;
		if (typeof date === "string") {
			return date.length >= 10 ? date.slice(0, 10) : date;
		}
		if (date instanceof Date && !Number.isNaN(date.getTime())) {
			return date.toISOString().slice(0, 10);
		}
		return null;
	}

	/**
	 * Extrae el rango de fechas del periodo planificado.
	 * Prioridad: required_needs → request.start/end → min/max de result_plans.
	 * @param {Object} response - Respuesta del cálculo IA
	 * @param {Object} request - Petición original del cálculo IA
	 * @param {Array} plans - result_plans ya extraídos
	 * @returns {{ start: string|null, end: string|null }}
	 */
	static extractPlanningDateRange(response, request, plans = []) {
		const requiredNeedsDates = this.extractDatesFromRequiredNeeds(
			response?.required_needs
		);
		if (requiredNeedsDates.length > 0) {
			return this.minMaxDates(requiredNeedsDates);
		}

		const requestStart = this.normalizePlanningDate(request?.start);
		const requestEnd = this.normalizePlanningDate(request?.end);
		if (requestStart && requestEnd) {
			return { start: requestStart, end: requestEnd };
		}

		const planDates = plans
			.map((plan) => this.normalizePlanningDate(plan.day))
			.filter(Boolean);
		if (planDates.length > 0) {
			return this.minMaxDates(planDates);
		}

		return { start: requestStart, end: requestEnd };
	}

	/**
	 * @param {Array} requiredNeeds
	 * @returns {string[]}
	 */
	static extractDatesFromRequiredNeeds(requiredNeeds) {
		if (!Array.isArray(requiredNeeds)) return [];

		const dates = new Set();
		requiredNeeds.forEach((shiftFunction) => {
			(shiftFunction?.values || []).forEach((item) => {
				const normalized = this.normalizePlanningDate(item?.date);
				if (normalized) dates.add(normalized);
			});
		});

		return Array.from(dates);
	}

	/**
	 * @param {string[]} dates
	 * @returns {{ start: string|null, end: string|null }}
	 */
	static minMaxDates(dates) {
		if (!dates.length) return { start: null, end: null };
		return {
			start: dates.reduce((min, date) => (date < min ? date : min)),
			end: dates.reduce((max, date) => (date > max ? date : max)),
		};
	}

	/**
	 * Extrae los IDs de todos los empleados presentes en el `request` del
	 * cálculo (el grupo completo enviado a la IA). El motor de planificación
	 * identifica a cada empleado mediante el campo `name` (que contiene el id
	 * del empleado), aunque también contemplamos `id`/`employee_id` por si el
	 * formato cambia.
	 * @param {Object} request - Objeto request ya parseado
	 * @returns {number[]} IDs de empleados únicos y ordenados
	 */
	static extractRequestEmployeeIds(request) {
		if (!request || !Array.isArray(request.employees)) return [];

		const ids = new Set();
		request.employees.forEach((emp) => {
			if (!emp) return;
			const rawId = emp.id ?? emp.employee_id ?? emp.name;
			const parsed = parseInt(rawId, 10);
			if (!Number.isNaN(parsed)) {
				ids.add(parsed);
			}
		});

		return Array.from(ids).sort((a, b) => a - b);
	}

	/**
	 * Versión más detallada que incluye información adicional
	 * @param {string|Object} jsonData - JSON string o objeto parseado
	 * @returns {Object} Objeto extendido con más información
	 */
	static parseDetailed(jsonData) {
		try {
			const data =
				typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
			const request =
				typeof data.request === "string"
					? JSON.parse(data.request)
					: data.request;
			const response =
				typeof data.response === "string"
					? JSON.parse(data.response)
					: data.response;

			const basicResult = this.parse(jsonData);
			if (!basicResult) return null;

			return {
				...basicResult,
				// Información adicional del proceso
				process_id: data.id,
				state: data.state,
				executor: {
					id: data.employee_id,
					name: data.employee_full_name,
				},
				timing: {
					start: data.start,
					end: data.end,
					duration:
						data.end && data.start
							? new Date(data.end) - new Date(data.start)
							: null,
				},
				// Estadísticas
				stats: {
					total_pending_needs: response.total_pending_needs || 0,
					total_required_needs: response.total_required_needs || 0,
					completion_rate: response.total_required_needs
						? parseFloat(
								((response.total_required_needs -
									response.total_pending_needs) /
									response.total_required_needs) *
									100
						  )
						: 0,
				},
				// Información de empleados del request
				employees_info: request.employees
					? request.employees.map((emp) => ({
							id: emp.name,
							max_hours_per_contract: emp.max_working_hours_per_contract,
							functions: emp.functions.map((f) => f.name),
					  }))
					: [],
			};
		} catch (error) {
			console.error("Error parsing detailed schedule JSON:", error);
			return null;
		}
	}

	/**
	 * Agrupa los planes por empleado
	 * @param {Array} plans - Array de planes
	 * @returns {Object} Objeto agrupado por employee_id
	 */
	static groupPlansByEmployee(plans) {
		return plans.reduce((acc, plan) => {
			if (!acc[plan.employee_id]) {
				acc[plan.employee_id] = [];
			}
			acc[plan.employee_id].push(plan);
			return acc;
		}, {});
	}

	/**
	 * Agrupa los planes por día
	 * @param {Array} plans - Array de planes
	 * @returns {Object} Objeto agrupado por day
	 */
	static groupPlansByDay(plans) {
		return plans.reduce((acc, plan) => {
			if (!acc[plan.day]) {
				acc[plan.day] = [];
			}
			acc[plan.day].push(plan);
			return acc;
		}, {});
	}

	/**
	 * Obtiene estadísticas de los planes
	 * @param {Array} plans - Array de planes
	 * @returns {Object} Estadísticas
	 */
	static getPlansStats(plans) {
		const byEmployee = this.groupPlansByEmployee(plans);
		const byDay = this.groupPlansByDay(plans);

		return {
			total_plans: plans.length,
			unique_employees: Object.keys(byEmployee).length,
			unique_days: Object.keys(byDay).length,
			plans_per_employee: Object.entries(byEmployee).map(
				([emp_id, emp_plans]) => ({
					employee_id: parseInt(emp_id),
					plan_count: emp_plans.length,
				})
			),
			plans_per_day: Object.entries(byDay).map(([day, day_plans]) => ({
				day,
				plan_count: day_plans.length,
			})),
		};
	}
}
